import { useEffect, useRef } from 'react';
import useSideEffects from '../global/useSideEffects';
import useRouter from '../navigation/useRouter';
import { SCREENS } from '../../services/exports/Constants';
import { Routes } from '../../modules/mobile/exports/Enums';

/**
 * Holds the table's visit check (SideEffectsProvider) while `inFlight`: a payment being placed or
 * confirmed is not turned into pickup under the guest. The check runs once the payment settles.
 */
export function useHoldTableVisitCheck(inFlight: boolean): void {
  const { holdTableVisitCheck } = useSideEffects();

  useEffect(() => {
    if (!inFlight) {
      return;
    }

    return holdTableVisitCheck();
  }, [inFlight, holdTableVisitCheck]);
}

/**
 * On /checkout and /payment: when the table's visit runs out there (a tab restored the next day, or
 * the 3 h passing on the page), SideEffectsProvider drops the table and this sends the guest to the
 * menu, which says only a new scan returns to the table. So does a visit that ran out before the
 * screen opened (on /order/{uuid}, say), unless the guest has been to the menu since.
 */
export function useLeaveCheckoutWhenTableVisitEnds(): void {
  const { registerTableVisitExit } = useSideEffects();
  const { navigate } = useRouter();
  // The router's navigate is a new function every render; the exit is registered once.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(
    () =>
      registerTableVisitExit(() =>
        navigateRef.current({ web: { to: SCREENS.HOME_SCREEN }, mobile: { path: Routes.Main } }),
      ),
    [registerTableVisitExit],
  );
}
